import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  Link,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import cwyLogo from '../../../assets/cw-red-logo.svg';
import { Footer } from '../../../components/layout/Footer';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { getRememberMePreference } from '../../../shared/services/authService';
import { requestAgentPhonePopupPermission } from '../../../services/agentPhone/agentPhoneBridge';

type LoginStep = 'credentials' | 'mfa';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyMfaLogin, resendMfaCode } = useAuth();
  const [step, setStep] = useState<LoginStep>('credentials');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [mfaCode, setMfaCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaMethod, setMfaMethod] = useState<'email' | 'totp'>('email');
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => getRememberMePreference());
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const message = params.get('message');
    if (message) {
      setError(message);
      window.history.replaceState({}, document.title, '/login');
    }
  }, [location]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const redirectAfterLogin = () => {
    requestAgentPhonePopupPermission();
    const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
    const redirectTo = from?.pathname
      ? `${from.pathname}${from.search ?? ''}`
      : '/';
    navigate(redirectTo, { replace: true });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setInfo('');

    try {
      const result = await login(formData.username, formData.password, rememberMe);
      if ('requiresMfa' in result && result.requiresMfa) {
        setMfaToken(result.mfaToken);
        setMfaMethod(result.mfaMethod);
        setMaskedEmail(result.maskedEmail);
        setMfaCode('');
        setStep('mfa');
        setTrustDevice(false);
        if (result.mfaMethod === 'email') {
          setInfo(result.maskedEmail
            ? `We sent a verification code to ${result.maskedEmail}`
            : 'We sent a verification code to your email');
        }
        return;
      }
      redirectAfterLogin();
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Failed to login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setInfo('');

    try {
      await verifyMfaLogin(mfaToken, mfaCode.trim(), { rememberMe, trustDevice });
      redirectAfterLogin();
    } catch (err: any) {
      console.error('MFA error:', err);
      setError(err.response?.data?.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || mfaMethod !== 'email') return;
    setError('');
    setInfo('');
    try {
      await resendMfaCode(mfaToken);
      setInfo('A new verification code has been sent.');
      setResendCooldown(60);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not resend code. Please try again.');
    }
  };

  const handleBackToLogin = () => {
    setStep('credentials');
    setMfaCode('');
    setMfaToken('');
    setTrustDevice(false);
    setError('');
    setInfo('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        position: 'fixed',
        top: 0,
        left: 0
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 3,
          mx: 2
        }}
      >
        <Box
          component="img"
          src={cwyLogo}
          alt="CWY Logo"
          sx={{
            width: 240,
            height: 'auto',
            mb: 3
          }}
        />

        <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
          {step === 'credentials' ? 'Welcome to CWY Admin Portal' : 'Verify your identity'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
            {error}
          </Alert>
        )}

        {info && (
          <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
            {info}
          </Alert>
        )}

        {step === 'credentials' ? (
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
              autoComplete="username"
              autoFocus
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  size="small"
                />
              }
              label="Remember me"
              sx={{ mb: 2, userSelect: 'none' }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                py: 1.5,
                bgcolor: '#f1f3f4',
                color: '#0f0f0f',
                '&:hover': {
                  bgcolor: '#e8eaed'
                }
              }}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleMfaSubmit} sx={{ width: '100%' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {mfaMethod === 'email'
                ? 'Enter the 6-digit code sent to your email.'
                : 'Enter the 6-digit code from your Authenticator App.'}
            </Typography>

            <TextField
              fullWidth
              label="Verification code"
              value={mfaCode}
              onChange={(e) => {
                setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                setError('');
              }}
              margin="normal"
              variant="outlined"
              autoComplete="one-time-code"
              autoFocus
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
              sx={{ mb: 1 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  size="small"
                />
              }
              label="Trust this device for 7 days"
              sx={{ mb: 2, userSelect: 'none' }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading || mfaCode.length !== 6}
              sx={{
                py: 1.5,
                mb: 1.5,
                bgcolor: '#f1f3f4',
                color: '#0f0f0f',
                '&:hover': { bgcolor: '#e8eaed' }
              }}
            >
              {isLoading ? 'Verifying...' : 'Verify and sign in'}
            </Button>

            {mfaMethod === 'email' && (
              <Button
                fullWidth
                variant="text"
                disabled={resendCooldown > 0}
                onClick={handleResend}
                sx={{ mb: 1 }}
              >
                {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
              </Button>
            )}

            <Button fullWidth variant="text" onClick={handleBackToLogin}>
              Back to login
            </Button>
          </Box>
        )}

        <Typography
          variant="caption"
          sx={{
            mt: 2,
            color: 'text.secondary',
            textAlign: 'center',
            fontSize: '0.75rem'
          }}
        >
          Having trouble? Contact{' '}
          <Link
            href="mailto:support@canadawaleyar.com"
            sx={{
              color: '#0f0f0f',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
                color: '#606060'
              }
            }}
          >
            support@canadawaleyar.com
          </Link>
        </Typography>
      </Paper>
      <Footer />
    </Box>
  );
};

export default LoginPage;
